import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Send, Loader2, Sparkles, BookOpen, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

const inputCls =
  "bg-white border-[#464646]/15 text-[#464646] placeholder:text-[#464646]/40 rounded-full px-5 h-11 text-sm focus-visible:ring-[#96aeb4]/40";

// ---------- Free Summary ----------
function FreeSummaryCard() {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!first.trim() || !last.trim() || !email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/ebook`, {
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim(),
      });
      toast.success(res.data.message);
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      variants={fadeUp}
      className="relative h-full glass rounded-2xl sm:rounded-3xl overflow-hidden flex flex-col"
      data-testid="free-summary-card"
    >
      <div
        className="h-1.5"
        style={{ background: "linear-gradient(90deg, #a6c1a8, #b3ccb8, #96aeb4)" }}
      />
      <div className="p-6 sm:p-8 md:p-10 flex flex-col flex-1">
        <div className="inline-flex items-center gap-2 bg-[#a6c1a8]/20 text-[#587e62] px-3 py-1.5 rounded-full mb-5 w-fit">
          <BookOpen size={13} />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] font-semibold">
            Free Summary
          </span>
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl md:text-[36px] font-bold text-[#464646] leading-[1.1] tracking-tight mb-4">
          Not ready yet?
          <br />
          <span className="italic text-[#7a9d83]">Try a free taste.</span>
        </h3>
        <p className="text-sm text-[#464646]/70 leading-relaxed mb-6">
          Get the free summary of <em>52 Ideas to Stay Calm While Accomplishing More</em>, a small daily pause, straight to your inbox.
        </p>

        <div className="mt-auto">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-2xl bg-[#a6c1a8]/15 border border-[#a6c1a8]/30 px-5 py-6 text-center"
              data-testid="free-summary-success"
            >
              <Sparkles size={22} className="inline text-[#7a9d83] mb-2" />
              <p className="font-serif text-lg text-[#464646] font-semibold mb-1">
                On its way.
              </p>
              <p className="text-xs text-[#464646]/65">
                Check your inbox in the next minute or two.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-2.5" data-testid="free-summary-form">
              <div className="grid grid-cols-2 gap-2.5">
                <Input
                  placeholder="First name"
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  className={inputCls}
                  data-testid="free-summary-first-input"
                />
                <Input
                  placeholder="Last name"
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                  className={inputCls}
                  data-testid="free-summary-last-input"
                />
              </div>
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="free-summary-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#464646] hover:bg-[#96aeb4] text-white rounded-full py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="free-summary-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />}
                {loading ? "Sending…" : "Email me the free summary"}
              </button>
              <p className="text-[10px] text-center text-[#464646]/40 pt-1">
                One email. No follow-ups unless you ask.
              </p>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Contact (Say Hello) ----------
function ContactCard() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/contact`, form);
      toast.success(res.data.message);
      setSent(true);
      setForm({ first_name: "", last_name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      variants={fadeUp}
      className="relative h-full glass rounded-2xl sm:rounded-3xl overflow-hidden flex flex-col"
      data-testid="contact-card"
    >
      <div
        className="h-1.5"
        style={{ background: "linear-gradient(90deg, #96aeb4, #a89b94, #b3ccb8)" }}
      />
      <div className="p-6 sm:p-8 md:p-10 flex flex-col flex-1">
        <div className="inline-flex items-center gap-2 bg-[#96aeb4]/15 text-[#5e7d84] px-3 py-1.5 rounded-full mb-5 w-fit">
          <MessageCircle size={13} />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] font-semibold">
            Connect
          </span>
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl md:text-[36px] font-bold text-[#464646] leading-[1.1] tracking-tight mb-4">
          Say hello.
        </h3>
        <p className="text-sm text-[#464646]/70 leading-relaxed mb-6">
          Questions about the deck, the sutras, or partnerships? We'd love to hear from you.
        </p>

        <div className="mt-auto">
          {sent ? (
            <div className="text-center py-4" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-[#a6c1a8]/30 flex items-center justify-center mx-auto mb-4">
                <Send size={20} className="text-[#7a9d83]" />
              </div>
              <h3 className="font-serif text-xl text-[#464646] font-medium mb-2">
                Message received
              </h3>
              <p className="text-sm text-[#464646]/65">
                Check your inbox for a confirmation, we'll be in touch soon.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-5 text-xs text-[#96aeb4] hover:text-[#7a9099] font-semibold uppercase tracking-[0.2em]"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-2.5">
                <Input
                  placeholder="First name"
                  value={form.first_name}
                  onChange={set("first_name")}
                  className={inputCls}
                  data-testid="contact-first-input"
                />
                <Input
                  placeholder="Last name"
                  value={form.last_name}
                  onChange={set("last_name")}
                  className={inputCls}
                  data-testid="contact-last-input"
                />
              </div>
              <Input
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                data-testid="contact-email-input"
              />
              <Textarea
                placeholder="Your message"
                value={form.message}
                onChange={set("message")}
                rows={3}
                className="bg-white border-[#464646]/15 text-[#464646] placeholder:text-[#464646]/40 rounded-2xl px-5 py-3 text-sm focus-visible:ring-[#96aeb4]/40 resize-none"
                data-testid="contact-message-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#464646] hover:bg-[#96aeb4] text-white rounded-full py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
                {loading ? "Sending…" : "Send message"}
              </button>
              <p className="text-[10px] text-center text-[#464646]/40 pt-1">
                We read every note.
              </p>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Combined section ----------
export default function ConnectSection() {
  return (
    <section
      id="connect-section"
      className="relative py-16 sm:py-20 md:py-24 px-5 md:px-10 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #fff8f3 0%, #f5efe7 50%, #fff8f3 100%)",
      }}
      data-testid="connect-section"
    >
      {/* Floating orbs */}
      <motion.div
        className="absolute -top-24 -left-16 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(168,155,148,0.18) 0%, transparent 70%)" }}
        animate={{ y: [0, 14, 0] }}
        transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-24 -right-16 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(150,174,180,0.18) 0%, transparent 70%)" }}
        animate={{ y: [0, -14, 0] }}
        transition={{ repeat: Infinity, duration: 14, ease: "easeInOut" }}
      />

      <div className="max-w-[1200px] mx-auto relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-stretch"
          id="contact"
        >
          <FreeSummaryCard />
          <ContactCard />
        </motion.div>
      </div>
    </section>
  );
}
